import { z } from "zod";

export const INSTRUCTOR_PAYOUT_WORK_KINDS = [
  "general_class",
  "private_lesson",
  "partner",
  "room_rental_assist",
  "general_class_assist",
] as const;

export type InstructorPayoutWorkKind =
  (typeof INSTRUCTOR_PAYOUT_WORK_KINDS)[number];

export const InstructorPayoutWorkKindSchema = z.enum(
  INSTRUCTOR_PAYOUT_WORK_KINDS,
);

export const INSTRUCTOR_PAYOUT_LOCATION_KINDS = [
  "studio",
  "off_site",
  "off_site_near",
  "off_site_far",
] as const;
export type InstructorPayoutLocationKind =
  (typeof INSTRUCTOR_PAYOUT_LOCATION_KINDS)[number];
export const InstructorPayoutLocationKindSchema = z.enum(
  INSTRUCTOR_PAYOUT_LOCATION_KINDS,
);

/** แถวอัตราที่รองรับในระบบ (partner / room_rental_assist ใช้ studio เท่านั้น) */
export const INSTRUCTOR_PAYOUT_RATE_SLOTS = [
  { workKind: "general_class", locationKind: "studio" },
  { workKind: "general_class", locationKind: "off_site" },
  { workKind: "private_lesson", locationKind: "studio" },
  { workKind: "private_lesson", locationKind: "off_site_near" },
  { workKind: "private_lesson", locationKind: "off_site_far" },
  { workKind: "partner", locationKind: "studio" },
  { workKind: "room_rental_assist", locationKind: "studio" },
  { workKind: "general_class_assist", locationKind: "studio" },
  { workKind: "general_class_assist", locationKind: "off_site" },
] as const satisfies ReadonlyArray<{
  workKind: InstructorPayoutWorkKind;
  locationKind: InstructorPayoutLocationKind;
}>;

export const INSTRUCTOR_PAYOUT_BASES = ["per_hour", "per_session"] as const;
export type InstructorPayoutBasis = (typeof INSTRUCTOR_PAYOUT_BASES)[number];
export const InstructorPayoutBasisSchema = z.enum(INSTRUCTOR_PAYOUT_BASES);

/** Default basis เมื่อสร้างครูใหม่ / เติม slot ว่าง — room assist ล็อก per_session */
export function defaultPayoutBasisForWorkKind(
  workKind: InstructorPayoutWorkKind,
): InstructorPayoutBasis {
  return workKind === "room_rental_assist" ||
    workKind === "general_class_assist"
    ? "per_session"
    : "per_hour";
}

/**
 * จำนวนเริ่มต้นสำหรับ room_rental_assist ตอนสร้างครูใหม่
 * (ตั้งทีละครู — ไม่มีอัตรากลางสตูดิโอใน MVP; แก้ค่านี้ถ้าธุรกิจมี template)
 */
export const DEFAULT_ROOM_RENTAL_ASSIST_AMOUNT_KIP = 0;

/**
 * จำนวนเริ่มต้นสำหรับ general_class_assist (TA) ตอนสร้างครูใหม่
 * basis = per_session; แก้ค่านี้ถ้าธุรกิจมี template กลางทีหลัง
 */
export const DEFAULT_GENERAL_CLASS_ASSIST_AMOUNT_KIP = 0;

/** Private Package ที่สตูดิโอ: เรทตายตัวตามจำนวนคน หรือเรทส่วนตัวของครู */
export const PRIVATE_PACKAGE_PAYOUT_MODES = [
  "package_tier",
  "instructor_rate",
] as const;
export type PrivatePackagePayoutMode =
  (typeof PRIVATE_PACKAGE_PAYOUT_MODES)[number];
export const PrivatePackagePayoutModeSchema = z.enum(
  PRIVATE_PACKAGE_PAYOUT_MODES,
);

/** Default quota สำหรับครู full_time ตอนสร้างใหม่ */
export const DEFAULT_MONTHLY_FREE_GENERAL_CLASS_SESSIONS_FULL_TIME = 8;

export function defaultMonthlyFreeGeneralClassSessions(
  employmentType: "full_time" | "freelance",
): number {
  return employmentType === "full_time"
    ? DEFAULT_MONTHLY_FREE_GENERAL_CLASS_SESSIONS_FULL_TIME
    : 0;
}

function defaultAmountKipForWorkKind(
  workKind: InstructorPayoutWorkKind,
): number {
  if (workKind === "room_rental_assist") {
    return DEFAULT_ROOM_RENTAL_ASSIST_AMOUNT_KIP;
  }
  if (workKind === "general_class_assist") {
    return DEFAULT_GENERAL_CLASS_ASSIST_AMOUNT_KIP;
  }
  return 0;
}

/** Template อัตราเริ่มต้นครบทุก slot ตอนสร้างครูใหม่ */
export function defaultInstructorPayoutRateTemplate(): Array<{
  workKind: InstructorPayoutWorkKind;
  locationKind: InstructorPayoutLocationKind;
  payoutBasis: InstructorPayoutBasis;
  amountKip: number;
}> {
  return INSTRUCTOR_PAYOUT_RATE_SLOTS.map((slot) => ({
    workKind: slot.workKind,
    locationKind: slot.locationKind,
    payoutBasis: defaultPayoutBasisForWorkKind(slot.workKind),
    amountKip: defaultAmountKipForWorkKind(slot.workKind),
  }));
}

export const InstructorPayoutRateItemSchema = z.object({
  workKind: InstructorPayoutWorkKindSchema,
  locationKind: InstructorPayoutLocationKindSchema.default("studio"),
  payoutBasis: InstructorPayoutBasisSchema.default("per_hour"),
  amountKip: z.number().int().min(0),
});
export type InstructorPayoutRateItemDTO = z.infer<
  typeof InstructorPayoutRateItemSchema
>;

/**
 * Instructor Schema - ครูสอน
 */
export const InstructorSchema = z.object({
  id: z.string(),
  name: z.string(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  employmentType: z.enum(["full_time", "freelance"]),
  /** Lead general_class sessions ฟรีต่อเดือน (retainer quota) */
  monthlyFreeGeneralClassSessions: z.number().int().min(0).max(999),
  /** Private Package ที่สตูดิโอ ใช้เรทตายตัว หรือเรทของครู */
  privatePackagePayoutMode: PrivatePackagePayoutModeSchema,
  /** ອັດຕາຄລາສທົ່ວໄປຕໍ່ຊົ່ວໂມງ (ກີບ) — ຄຳນວນຈາກ payoutRates ບໍ່ມີຄອລັມ DB */
  ratePerSession: z.number().int().nullable(),
  payoutRates: z.array(InstructorPayoutRateItemSchema),
  status: z.enum(["active", "inactive"]),
  notes: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type InstructorDTO = z.infer<typeof InstructorSchema>;

/**
 * Create Instructor Schema
 */
export const CreateInstructorSchema = z.object({
  name: z.string().min(1, "ຊື່ຕ້ອງບໍ່ວ່າງ"),
  phoneNumber: z.string().nullable().optional(),
  email: z.string().email("ຮູບແບບອີເມວບໍ່ຖືກຕ້ອງ").nullable().optional(),
  bankAccountNumber: z.string().nullable().optional(),
  employmentType: z.enum(["full_time", "freelance"]),
  monthlyFreeGeneralClassSessions: z.number().int().min(0).max(999).optional(),
  privatePackagePayoutMode: PrivatePackagePayoutModeSchema.optional(),
  /** ອັດຕາຕໍ່ຊົ່ວໂມງແຍກຕາມປະເພດວຽກ */
  payoutRates: z.array(InstructorPayoutRateItemSchema).optional(),
  /** @deprecated — ຖ້າບໍ່ສົ່ງ payoutRates ຈະແຜ່ຄ່ານີ້ໄປທັງ 3 ປະເພດ */
  ratePerSession: z.number().int().positive().nullable().optional(),
  status: z.enum(["active", "inactive"]).default("active"),
  notes: z.string().nullable().optional(),
});
export type CreateInstructorDTO = z.infer<typeof CreateInstructorSchema>;

/**
 * Update Instructor Schema
 */
export const UpdateInstructorSchema = z.object({
  name: z.string().min(1, "ຊື່ຕ້ອງບໍ່ວ່າງ").optional(),
  phoneNumber: z.string().nullable().optional(),
  email: z.string().email("ຮູບແບບອີເມວບໍ່ຖືກຕ້ອງ").nullable().optional(),
  bankAccountNumber: z.string().nullable().optional(),
  employmentType: z.enum(["full_time", "freelance"]).optional(),
  monthlyFreeGeneralClassSessions: z.number().int().min(0).max(999).optional(),
  privatePackagePayoutMode: PrivatePackagePayoutModeSchema.optional(),
  payoutRates: z.array(InstructorPayoutRateItemSchema).optional(),
  ratePerSession: z.number().int().positive().nullable().optional(),
  status: z.enum(["active", "inactive"]).optional(),
  notes: z.string().nullable().optional(),
});
export type UpdateInstructorDTO = z.infer<typeof UpdateInstructorSchema>;

/**
 * Instructor Summary DTO - สถิติการทำงานของผู้สอน
 */
export type InstructorSummaryDTO = {
  classSessions: {
    total: number;
    scheduled: number;
    completed: number;
    cancelled: number;
  };
  privateLessons: {
    total: number;
    scheduled: number;
    completed: number;
    cancelled: number;
  };
  checkIns: {
    total: number;
    classSessions: number;
    privateLessons: number;
  };
  /** ຄ່າຈ້າງຄາດຄະເນ ຈາກ session ສຳເລັດ — ຕາມ payout_basis + amount_kip ແຕ່ລະປະເພດ */
  revenue: number;
  lastClassSession: string | null; // YYYY-MM-DD
  lastPrivateLesson: string | null; // YYYY-MM-DD
};

/**
 * Instructor Class Session DTO - รายการ class sessions ที่ผู้สอนสอน
 */
export type InstructorClassSessionDTO = {
  id: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm:ss
  endTime: string; // HH:mm:ss
  className: string; // "classType - level"
  roomName: string;
  status: "scheduled" | "completed" | "cancelled";
  attendanceCount: number;
  hasCheckIn: boolean;
};

/**
 * Instructor Private Lesson DTO - รายการ private lessons ที่ผู้สอนสอน
 */
export type InstructorPrivateLessonDTO = {
  id: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm:ss
  endTime: string; // HH:mm:ss
  studentName: string;
  studentMemberId: string;
  roomName: string;
  durationMinutes: number;
  status: "scheduled" | "completed" | "cancelled";
  hasCheckIn: boolean;
};

/**
 * Instructor Check-in DTO - รายการ check-ins ของผู้สอน
 */
export type InstructorCheckInDTO = {
  id: string;
  checkedInAt: string; // ISO timestamp
  sessionType: "general_class" | "private_lesson" | "room_booking";
  sessionName: string; // "classType - level" / "studentName - date" / room booking title
  checkedInByName: string;
  isSubstitute: boolean | null;
  substituteForInstructorId: string | null;
  substituteForInstructorName: string | null;
  substituteReason: string | null;
};
